import {
  CONSENT_STORAGE_KEY,
  CONSENT_VALUE,
  COOKIESCRIPT_OBSERVE_TIMEOUT_MS,
  COOKIESCRIPT_WRAPPER_ID,
} from './consts';

/**
 * Whether the first-party consent banner owns cookie consent on the given host.
 *
 * The portal loads a third-party Cookie-Script banner via GTM that writes its
 * consent cookie scoped to `.qodo.ai`. On any non-`qodo.ai` host that cookie is
 * rejected by the browser, so Cookie-Script can never persist a dismissal and
 * its banner reappears on every navigation/reload (PER-51). We take over
 * consent only on those hosts:
 *
 * - `*.codium-inc.com` (dev / dkredo / ephemeral preview envs) → `true`.
 * - `*.qodo.ai` (production) → `false`: Cookie-Script works there, untouched.
 * - `localhost` / `127.0.0.1` → `false`: GTM is not loaded locally, so there is
 *   no cookie banner to replace.
 */
export const isFirstPartyConsentHost = (hostname: string): boolean =>
  hostname.endsWith('.codium-inc.com') || hostname === 'codium-inc.com';

/** Whether the user has already recorded a cookie-consent choice. */
export const hasStoredConsent = (): boolean => {
  try {
    return localStorage.getItem(CONSENT_STORAGE_KEY) !== null;
  } catch {
    // Private-mode / storage-disabled: treat as no stored choice.
    return false;
  }
};

/** Persists the user's cookie-consent choice. */
export const storeConsent = (
  value: (typeof CONSENT_VALUE)[keyof typeof CONSENT_VALUE],
): void => {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, value);
  } catch {
    // Best-effort: if storage is unavailable the banner simply re-shows next
    // load, which is acceptable degradation and never throws into render.
  }
};

/**
 * Hides the Cookie-Script wrapper node. Uses an `!important` inline priority so
 * it overrides any inline `display` Cookie-Script sets on its own wrapper.
 */
const hideCookieScriptWrapper = (wrapper: HTMLElement): void => {
  wrapper.style.setProperty('display', 'none', 'important');
};

/**
 * Suppresses the third-party Cookie-Script banner on managed hosts, robust to
 * its asynchronous GTM injection.
 *
 * Cookie-Script appends its wrapper (`#cookiescript_injected_wrapper`) to
 * `document.body` some time after first paint, so a one-shot check on mount
 * usually runs before the node exists and misses it — leaving both banners
 * visible. We hide it immediately if it is already present, otherwise observe
 * `document.body` for it to appear and hide it on arrival. The observer
 * disconnects as soon as the wrapper is hidden, on a safety timeout (so a
 * blocked/never-loaded GTM leaves nothing watching), and via the returned
 * teardown on unmount.
 *
 * @returns a teardown function that disconnects the observer and clears the
 * timeout; safe to call more than once.
 */
export const suppressCookieScriptBanner = (): (() => void) => {
  const body = document.body;

  const existing = document.getElementById(COOKIESCRIPT_WRAPPER_ID);

  if (existing instanceof HTMLElement) {
    hideCookieScriptWrapper(existing);

    return () => {};
  }

  // No body or no MutationObserver (non-DOM/legacy env): nothing to observe.
  if (!body || typeof MutationObserver === 'undefined') {
    return () => {};
  }

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let observer: MutationObserver | undefined;

  const teardown = (): void => {
    observer?.disconnect();

    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
      timeoutId = undefined;
    }
  };

  observer = new MutationObserver(() => {
    const wrapper = document.getElementById(COOKIESCRIPT_WRAPPER_ID);

    if (wrapper instanceof HTMLElement) {
      hideCookieScriptWrapper(wrapper);
      teardown();
    }
  });

  observer.observe(body, { childList: true });
  timeoutId = setTimeout(teardown, COOKIESCRIPT_OBSERVE_TIMEOUT_MS);

  return teardown;
};
